import { Box, MenuItem } from '@mui/material';
import { GaugeContainer, GaugeReferenceArc, useGaugeState } from '@mui/x-charts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLiveGraph } from '../LiveGraphStore';
import { Trans, useTranslation } from 'react-i18next';
import { CHART_RED } from '../../../theme/theme-colors';
import type { Graph } from '../dashboardTypes';
import { useWidgetHeader } from './WidgetHeaderContext';
import { usePersistedWidgetStyle } from './usePersistedWidgetStyle';
import RawDataWidget from './RawDataWidget';
import LiveNumberWidget from './LiveNumberWidget';

const MAX_VELOCITY_MPS = 1.4; // m/s
const START_ANGLE_DEG = -110;
const END_ANGLE_DEG = 110;
const MAJOR_TICK_STEP_MPS = 0.2;
const MINOR_TICK_STEP_MPS = MAJOR_TICK_STEP_MPS / 2;
const ARC_THICKNESS_PX = 10;
const VELOCITY_MAGNITUDE_DATA_KEY = 'Velocity Magnitude';

/** Upper bound for tick loops so the last tick is included despite float error. */
const MAX_VELOCITY_MPS_LOOP_EPSILON = 1e-9;

/** Space between the square container edge and the gauge arc (half-size minus this). */
const GAUGE_OUTER_RADIUS_INSET_PX = 8;

/** Pointer hub (filled circle at pivot). */
const POINTER_HUB_RADIUS_PX = 5;
/** Pointer needle line width. */
const POINTER_STROKE_WIDTH_PX = 3;
/** Pointer rotation animation. */
const POINTER_ROTATE_MS = 300;
const POINTER_ROTATE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

/** Radial tick length as a multiple of arc band thickness (ticks extend past the arc). */
const TICK_LENGTH_AS_MULTIPLE_OF_ARC_THICKNESS = 1.6;
const TICK_STROKE_WIDTH_MIN_PX = 1.5;
/** Scale tick stroke with gauge size. */
const TICK_STROKE_WIDTH_PER_OUTER_RADIUS = 0.018;
/** Where tick outer end sits relative to gauge outer radius (slightly inside the arc). */
const TICK_OUTER_END_RADIUS_RATIO_OF_OUTER = 0.98;

/** Major tick labels sit inside the arc by this fraction of inner radius. */
const TICK_LABEL_RADIUS_RATIO_OF_INNER_RADIUS = 0.9;
/** Vertical offset of the large center readout from the gauge center (fraction of outer radius). */
const CENTER_VALUE_BASELINE_OFFSET_RATIO_OF_OUTER = 0.62;
const TICK_LABEL_FONT_MIN_PX = 10;
const TICK_LABEL_FONT_SIZE_PER_OUTER_RADIUS = 0.095;
const CENTER_VALUE_FONT_MIN_PX = 18;
const CENTER_VALUE_FONT_SIZE_PER_OUTER_RADIUS = 0.18;

/** Maps to `palette.gauge.base` (CSS variables theme tracks light/dark). */
const GAUGE_BASE_COLOR_CSS_VAR = 'var(--mui-palette-gauge-base)';

const EMPTY_SPEED_GAUGE_GRAPH: Graph = {
  key: 'vel',
  title: '',
  series: [{ dataKey: VELOCITY_MAGNITUDE_DATA_KEY, color: CHART_RED, showMark: false, curve: 'linear' }],
  data: [],
};

const MAJOR_TICK_VALUES: number[] = (() => {
  const ticks: number[] = [];
  for (let v = 0; v <= MAX_VELOCITY_MPS + MAX_VELOCITY_MPS_LOOP_EPSILON; v += MAJOR_TICK_STEP_MPS) {
    ticks.push(Number(v.toFixed(1)));
  }
  return ticks;
})();

const ALL_TICK_VALUES: number[] = (() => {
  const ticks: number[] = [];
  for (let v = 0; v <= MAX_VELOCITY_MPS + MAX_VELOCITY_MPS_LOOP_EPSILON; v += MINOR_TICK_STEP_MPS) {
    ticks.push(Number(v.toFixed(1)));
  }
  return ticks;
})();

export function buildSpeedGaugeGraph(velGraph: Graph | undefined, speedTitle: string): Graph {
  const base = velGraph ?? EMPTY_SPEED_GAUGE_GRAPH;
  const series = base.series.filter((item) => item.dataKey === VELOCITY_MAGNITUDE_DATA_KEY);

  return {
    ...base,
    title: speedTitle,
    series: series.length > 0 ? series : EMPTY_SPEED_GAUGE_GRAPH.series,
    data: velGraph?.data ?? [],
  };
}

function GaugePointer({ color }: { color: string }) {
  const { valueAngle, outerRadius, cx, cy } = useGaugeState();

  if (valueAngle === null) {
    return null;
  }

  const valueAngleDeg = (valueAngle * 180) / Math.PI;
  return (
    <g
      style={{
        transformOrigin: `${cx}px ${cy}px`,
        transform: `rotate(${valueAngleDeg}deg)`,
        transition: `transform ${POINTER_ROTATE_MS}ms ${POINTER_ROTATE_EASING}`,
      }}
    >
      <circle cx={cx} cy={cy} r={POINTER_HUB_RADIUS_PX} fill={color} />
      <path d={`M ${cx} ${cy} L ${cx} ${cy - outerRadius}`} stroke={color} strokeWidth={POINTER_STROKE_WIDTH_PX} />
    </g>
  );
}

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

function gaugeAngleToCartesian(args: { angleDeg: number; cx: number; cy: number; radius: number }): { x: number; y: number } {
  const angleRad = toRadians(args.angleDeg - 90);
  return {
    x: args.cx + args.radius * Math.cos(angleRad),
    y: args.cy + args.radius * Math.sin(angleRad),
  };
}

interface GaugeWidgetProps {
  id: string;
  style: string;
}

const GaugeWidget = ({ id, style }: GaugeWidgetProps) => {
  const { t } = useTranslation();
  const velGraph = useLiveGraph('vel');
  const speedTitle = t('SpeedGaugeWidget.title', 'Speed');

  const { widgetStyle, handleSetStyle, styleMenuItems } = usePersistedWidgetStyle(id, style);

  const graph = useMemo(() => buildSpeedGaugeGraph(velGraph, speedTitle), [velGraph, speedTitle]);

  const menuItems = useMemo(
    () => (
      <>
        <MenuItem onClick={() => handleSetStyle('default')}>
          <Trans i18nKey="gaugeWidget.menuItem.default">Speed Gauge</Trans>
        </MenuItem>
        {styleMenuItems}
      </>
    ),
    [handleSetStyle, styleMenuItems]
  );

  useWidgetHeader({ title: graph.title, menuItems }, [graph.title, menuItems]);

  if (widgetStyle === 'raw') {
    return <RawDataWidget graph={graph} />;
  }

  if (widgetStyle === 'number') {
    return <LiveNumberWidget graph={graph} />;
  }

  return <SpeedGaugeBody graph={graph} />;
};

/** Arc gauge UI only — not mounted for raw/number alternate views (avoids ResizeObserver work). */
function SpeedGaugeBody({ graph }: { graph: Graph }) {
  const { t } = useTranslation();
  const tickLabelColor = 'var(--mui-palette-text-primary)';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState(0);

  const data = graph.data ?? [];
  const lastDatum = data.length ? (data[data.length - 1] as Record<string, unknown>) : null;
  const lastValue = lastDatum && typeof lastDatum[VELOCITY_MAGNITUDE_DATA_KEY] === 'number' ? (lastDatum[VELOCITY_MAGNITUDE_DATA_KEY] as number) : 0;

  const metersPerSecond = Number.isFinite(lastValue) ? Math.max(0, lastValue) : 0;
  const valueMps = Math.min(MAX_VELOCITY_MPS, metersPerSecond);

  const formatNumber = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1));

  const unitLabel = t('units.velocity.mps', 'm/s');
  const valueWithUnit = t('gaugeWidget.valueWithUnit', '{{value}} {{unit}}', {
    value: formatNumber(metersPerSecond),
    unit: unitLabel,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize(Math.max(0, Math.floor(Math.min(rect.width, rect.height))));
    };

    updateSize();

    const ro = new ResizeObserver(() => updateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const outerRadius = containerSize ? Math.max(1, Math.floor(containerSize / 2) - GAUGE_OUTER_RADIUS_INSET_PX) : undefined;
  const innerRadius = outerRadius ? Math.max(1, outerRadius - ARC_THICKNESS_PX) : undefined;

  return (
    <Box
      ref={containerRef}
      className="widget-body"
      flex="1"
      minHeight={0}
      overflow="hidden"
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {containerSize > 0 && outerRadius && innerRadius && (
        <GaugeContainer
          width={containerSize}
          height={containerSize}
          startAngle={START_ANGLE_DEG}
          endAngle={END_ANGLE_DEG}
          value={valueMps}
          valueMin={0}
          valueMax={MAX_VELOCITY_MPS}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
        >
          <GaugeReferenceArc />
          <GaugeTicks tickValues={ALL_TICK_VALUES} color={GAUGE_BASE_COLOR_CSS_VAR} />
          <GaugePointer color={CHART_RED} />
          <GaugeLabels
            tickValues={MAJOR_TICK_VALUES}
            formatTick={formatNumber}
            valueWithUnit={valueWithUnit}
            textColor={'var(--mui-palette-text-primary)'}
            tickLabelColor={tickLabelColor}
          />
        </GaugeContainer>
      )}
    </Box>
  );
}

function GaugeTicks({ tickValues, color }: { tickValues: number[]; color: string }) {
  const { outerRadius, innerRadius, cx, cy } = useGaugeState();

  if (outerRadius === null || innerRadius === null || cx === null || cy === null) return null;

  const arcThickness = outerRadius - innerRadius;
  const tickLength = arcThickness * TICK_LENGTH_AS_MULTIPLE_OF_ARC_THICKNESS;
  const tickStrokeWidth = Math.max(TICK_STROKE_WIDTH_MIN_PX, outerRadius * TICK_STROKE_WIDTH_PER_OUTER_RADIUS);
  const tickOuterRadius = outerRadius * TICK_OUTER_END_RADIUS_RATIO_OF_OUTER;
  const tickInnerRadius = tickOuterRadius - tickLength;

  return (
    <g>
      {tickValues.map((tick) => {
        const tNorm = tick / MAX_VELOCITY_MPS;
        const angleDeg = START_ANGLE_DEG + tNorm * (END_ANGLE_DEG - START_ANGLE_DEG);

        const p1 = gaugeAngleToCartesian({ angleDeg, cx, cy, radius: tickInnerRadius });
        const p2 = gaugeAngleToCartesian({ angleDeg, cx, cy, radius: tickOuterRadius });

        return <line key={tick} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={color} strokeWidth={tickStrokeWidth} strokeLinecap="round" />;
      })}
    </g>
  );
}

function GaugeLabels({
  tickValues,
  formatTick,
  valueWithUnit,
  textColor,
  tickLabelColor,
}: {
  tickValues: number[];
  formatTick: (value: number) => string;
  valueWithUnit: string;
  textColor: string;
  tickLabelColor: string;
}) {
  const { outerRadius, innerRadius, cx, cy } = useGaugeState();

  if (outerRadius === null || innerRadius === null || cx === null || cy === null) return null;

  const labelRadius = innerRadius * TICK_LABEL_RADIUS_RATIO_OF_INNER_RADIUS;
  const valueY = cy + outerRadius * CENTER_VALUE_BASELINE_OFFSET_RATIO_OF_OUTER;

  return (
    <g>
      {tickValues.map((tick) => {
        const tNorm = tick / MAX_VELOCITY_MPS;
        const angleDeg = START_ANGLE_DEG + tNorm * (END_ANGLE_DEG - START_ANGLE_DEG);
        const { x, y } = gaugeAngleToCartesian({ angleDeg, cx, cy, radius: labelRadius });

        return (
          <text
            key={tick}
            x={x}
            y={y}
            fill={tickLabelColor}
            fontSize={Math.max(TICK_LABEL_FONT_MIN_PX, outerRadius * TICK_LABEL_FONT_SIZE_PER_OUTER_RADIUS)}
            fontWeight={600}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {formatTick(tick)}
          </text>
        );
      })}

      <text
        x={cx}
        y={valueY}
        fill={textColor}
        fontSize={Math.max(CENTER_VALUE_FONT_MIN_PX, outerRadius * CENTER_VALUE_FONT_SIZE_PER_OUTER_RADIUS)}
        fontWeight={700}
        textAnchor="middle"
      >
        {valueWithUnit}
      </text>
    </g>
  );
}

export default GaugeWidget;
